/**
 * Slice LEVI-VOICE-01 · Levi voice preferences settings surface.
 *
 * Five controls:
 *  1. TTS on/off toggle
 *  2. Language (EN / ES)
 *  3. Gender preference (Friendly female / Friendly male / No preference)
 *  4. Speaking rate slider (0.8× – 1.2×)
 *  5. Voice preview button
 *
 * Changes apply immediately — no Save button. All prefs live in
 * leviVoicePrefs (localStorage-backed). No network calls.
 */
import { useEffect, useState } from "react";
import { Volume2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import {
  GENDER_CHOICES,
  LANGUAGE_CHOICES,
  RATE_BOUNDS,
  getGenderPreference,
  getLanguage,
  getSpeakingRate,
  getTtsEnabled,
  setGenderPreference,
  setLanguage,
  setSpeakingRate,
  setTtsEnabled,
  subscribeVoicePrefs,
} from "@/lib/leviVoicePrefs";
import { speak, stopSpeaking, primeTts } from "@/lib/leviSpeech";

const PREVIEW_TEXT = {
  en: "Hi, I'm Levi. This is a quick voice preview.",
  es: "Hola, soy Levi. Esta es una vista previa de la voz.",
};

export default function LeviVoiceSettings() {
  const { t } = useLang();
  const [ttsOn, setTtsOn] = useState(getTtsEnabled());
  const [lang, setLangLocal] = useState(getLanguage("en"));
  const [gender, setGender] = useState(getGenderPreference());
  const [rate, setRate] = useState(getSpeakingRate());

  useEffect(() => {
    const unsub = subscribeVoicePrefs(() => {
      setTtsOn(getTtsEnabled());
      setLangLocal(getLanguage("en"));
      setGender(getGenderPreference());
      setRate(getSpeakingRate());
    });
    return unsub;
  }, []);

  const onToggle = () => {
    const next = !ttsOn;
    setTtsEnabled(next);
    if (!next) stopSpeaking("levi_voice_settings_toggle_off");
  };

  const onPreview = () => {
    primeTts("levi_voice_preview");
    speak(PREVIEW_TEXT[lang] || PREVIEW_TEXT.en, lang);
  };

  return (
    <section
      className="jc-card p-5"
      data-testid="levi-voice-settings"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-blue-100 p-2 shrink-0">
          <Volume2 size={16} className="text-blue-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline">{t("levi_voice.overline")}</div>
          <h3 className="mt-1 text-base font-display">
            {t("levi_voice.title")}
          </h3>
          <p className="mt-1 text-sm text-[var(--jc-text-soft)]">
            {t("levi_voice.description")}
          </p>

          {/* 1. TTS on/off */}
          <div className="mt-4 flex items-center justify-between border-b border-[var(--jc-border)] py-3">
            <label htmlFor="levi-voice-tts-toggle" className="text-sm">
              {t("levi_voice.tts_enabled_label")}
            </label>
            <button
              id="levi-voice-tts-toggle"
              data-testid="levi-voice-tts-toggle"
              type="button"
              onClick={onToggle}
              className={
                "relative inline-flex h-6 w-11 items-center rounded-full transition " +
                (ttsOn ? "bg-[var(--jc-blue)]" : "bg-[var(--jc-border)]")
              }
              aria-pressed={ttsOn}
              aria-label={t("levi_voice.tts_enabled_label")}
            >
              <span
                className={
                  "inline-block h-4 w-4 transform rounded-full bg-white transition " +
                  (ttsOn ? "translate-x-6" : "translate-x-1")
                }
              />
            </button>
          </div>

          {/* 2. Language */}
          <div className="mt-3 flex items-center justify-between border-b border-[var(--jc-border)] py-3">
            <label htmlFor="levi-voice-language-select" className="text-sm">
              {t("levi_voice.language_label")}
            </label>
            <select
              id="levi-voice-language-select"
              data-testid="levi-voice-language-select"
              value={lang}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={!ttsOn}
              className="border border-[var(--jc-border)] rounded-md px-2 py-1 text-sm bg-white disabled:opacity-50"
            >
              {LANGUAGE_CHOICES.map((l) => (
                <option key={l} value={l}>
                  {t(`levi_voice.language_option_${l}`)}
                </option>
              ))}
            </select>
          </div>

          {/* 3. Gender preference */}
          <div className="mt-3 flex items-center justify-between border-b border-[var(--jc-border)] py-3">
            <label htmlFor="levi-voice-gender-select" className="text-sm">
              {t("levi_voice.gender_label")}
            </label>
            <select
              id="levi-voice-gender-select"
              data-testid="levi-voice-gender-select"
              value={gender}
              onChange={(e) => setGenderPreference(e.target.value)}
              disabled={!ttsOn}
              className="border border-[var(--jc-border)] rounded-md px-2 py-1 text-sm bg-white disabled:opacity-50"
            >
              {GENDER_CHOICES.map((g) => (
                <option key={g} value={g}>
                  {t(`levi_voice.gender_option_${g}`)}
                </option>
              ))}
            </select>
          </div>

          {/* 4. Speaking rate */}
          <div className="mt-3 border-b border-[var(--jc-border)] py-3">
            <div className="flex items-center justify-between">
              <label htmlFor="levi-voice-rate-slider" className="text-sm">
                {t("levi_voice.rate_label")}
              </label>
              <span
                className="text-xs text-[var(--jc-text-soft)]"
                data-testid="levi-voice-rate-value"
              >
                {rate.toFixed(2)}×
              </span>
            </div>
            <input
              id="levi-voice-rate-slider"
              data-testid="levi-voice-rate-slider"
              type="range"
              min={RATE_BOUNDS.min}
              max={RATE_BOUNDS.max}
              step={RATE_BOUNDS.step}
              value={rate}
              disabled={!ttsOn}
              onChange={(e) => setSpeakingRate(e.target.value)}
              className="mt-2 w-full disabled:opacity-50"
            />
            <div className="mt-1 flex justify-between text-[10px] text-[var(--jc-text-soft)]">
              <span>{t("levi_voice.rate_slower")}</span>
              <span>{t("levi_voice.rate_default")}</span>
              <span>{t("levi_voice.rate_faster")}</span>
            </div>
          </div>

          {/* 5. Preview */}
          <div className="mt-4">
            <button
              type="button"
              data-testid="levi-voice-preview-button"
              onClick={onPreview}
              disabled={!ttsOn}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--jc-border)] px-3 py-1.5 text-xs font-semibold text-[var(--jc-text)] disabled:opacity-50"
            >
              <Volume2 size={12} /> {t("levi_voice.preview_button")}
            </button>
            {!ttsOn && (
              <p
                className="mt-2 text-[11px] text-[var(--jc-text-soft)]"
                data-testid="levi-voice-disabled-note"
              >
                {t("levi_voice.disabled_note")}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
